import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Row,
  Col,
  Card,
  Typography,
  Divider,
  Space,
  Tooltip
} from "antd";
import { QuestionCircleOutlined } from "@ant-design/icons";
import { Line } from "@ant-design/plots";
import moment from "moment";
import FileConstants from "../../../../FileConstants";
import ENVIRONMENT from '../../../../environments';
import axios from 'axios';
import ViewTypeSelector from "./ViewTypeSelector";
import { processChartData, addWeekendAnnotations } from "./chartUtils";
import CalendarView from "./CalendarView";


const { Title } = Typography;

const DATE_FORMAT = "YYYY/MM/DD";

const CompanyChart = ({ 
  chartStyle, 
  companyList, 
  setIsLoading, 
  masterDateRange = null, 
  masterPresetRange = null,
  masterCompanyFilter = null
}) => {
  const [viewType, setViewType] = useState("Daily");
  const [companyChartData, setCompanyChartData] = useState([]);

  const fetchCompanyChartData = useCallback(async () => {
    try {
      setIsLoading(true);
      let startDate, endDate;
      
      // Always use master date range
      startDate = moment(masterDateRange?.start).format("YYYY-MM-DD");
      endDate = moment(masterDateRange?.end).format("YYYY-MM-DD");
      
      let payload = {
        action: "get_products_models_delivered_throughput",
        start_date: startDate,
        end_date: endDate
      };

      if (masterCompanyFilter && masterCompanyFilter !== "ALL") {
        payload.company_id = masterCompanyFilter;
      }

      const response = await axios.post(ENVIRONMENT.GET_SYSTEM_ANALYTICS, payload);
      console.log("Company chart data response:", response.data);
      setCompanyChartData(response.data || []);
    } catch (error) {
      console.error("Error fetching company chart data:", error);
      setCompanyChartData([]);
    } finally {
      setIsLoading(false);
    }
  }, [masterDateRange, masterCompanyFilter, setIsLoading]);

  useEffect(() => {
    fetchCompanyChartData();
  }, [fetchCompanyChartData]);

  const filteredCompanyProductData = useMemo(() => {
    return processChartData(
      companyChartData,
      viewType,
      "YYYY-MM-DD",
      masterDateRange?.start ? moment(masterDateRange.start).format("YYYY-MM-DD") : null,
      masterDateRange?.end ? moment(masterDateRange.end).format("YYYY-MM-DD") : null
    );
  }, [companyChartData, viewType, masterDateRange]);

  console.log("Filtered Company Product Data:", filteredCompanyProductData);

  const companyChartConfig = useMemo(() => {
    const baseConfig = FileConstants.LINE_PLOT_CONFIG(
      filteredCompanyProductData,
      "date2",
      "value",
      "type"
    );
    
    // Add weekend annotations only for daily view
    return addWeekendAnnotations(baseConfig, filteredCompanyProductData, "date2", viewType);
  }, [filteredCompanyProductData, viewType]);

  return (
    <Col span={24}>
      <Card style={chartStyle}>
        <Row justify="space-between" align="middle">
          <Title level={4}>Models Delivery Throughput{' '}
              <Tooltip title="Volume of unique customer model requests. Each request is counted once, therefore the models are approved models. If a model is rejected >1 time, the throughput will count the model once">
                <QuestionCircleOutlined style={{ color: '#8c8c8c' }} />
              </Tooltip> 
          </Title>
          <Row gutter={12}>
            <Col>
              <ViewTypeSelector
                value={viewType}
                onChange={setViewType}
              />
            </Col>
          </Row>
        </Row>

        <Divider style={{ margin: "16px 0" }} />
        <Line {...companyChartConfig} />
        
        <CalendarView
          viewType={viewType}
          chartData={filteredCompanyProductData}
          rawChartData={companyChartData}
          effectiveStartRange={masterDateRange?.start}
          effectiveEndRange={masterDateRange?.end}
        />
      </Card>
    </Col>
  );
};

export default CompanyChart;
